<script lang="ts" setup>
import { ref, computed } from "vue";
import { useRouter } from "vue-router";
import PanelGrid from "@/views/shared/components/PanelGrid.vue";
import PanelCard from "@/views/shared/components/PanelCard.vue";
import PageHeader from "@/components/PageHeader.vue";
import DataView from "@/components/DataView.vue";
import ErrorState from "@/views/shared/components/ErrorState.vue";
import type { PartnerClient } from "@/types/project-types";
import type { Config } from "datatables.net";
import { usePartnerClientSummaries } from "@/queries/partner-clients";

const router = useRouter();
const searchQuery = ref("");
const selectedStatus = ref("all");
const selectedHealth = ref("all");

// Real client summaries (shared with the dashboard + detail page).
const { clients: partnerClients, clientsLoading, clientsError, refetch } = usePartnerClientSummaries();
const isLoading = computed(() => clientsLoading.value);
const hasError = computed(() => !!clientsError.value);

const statusOptions = [
  { value: "all", label: "All Status" },
  { value: "active", label: "Active" },
  { value: "pending", label: "Pending" },
  { value: "inactive", label: "Inactive" },
];

const healthOptions = [
  { value: "all", label: "All Health" },
  { value: "healthy", label: "Healthy" },
  { value: "warning", label: "Warning" },
  { value: "error", label: "Error" },
];

const filteredClients = computed(() => {
  let filtered = partnerClients.value;

  if (searchQuery.value) {
    const query = searchQuery.value.toLowerCase();
    filtered = filtered.filter(
      (client) =>
        client.organizationName.toLowerCase().includes(query) ||
        client.contactName.toLowerCase().includes(query) ||
        client.contactEmail.toLowerCase().includes(query)
    );
  }

  if (selectedStatus.value !== "all") {
    filtered = filtered.filter((client) => client.status === selectedStatus.value);
  }

  if (selectedHealth.value !== "all") {
    filtered = filtered.filter((client) => client.healthStatus === selectedHealth.value);
  }

  return filtered;
});

function getHealthStatusColor(status: string) {
  switch (status) {
    case "healthy": return "text-success";
    case "warning": return "text-warning";
    case "error": return "text-danger";
    default: return "text-secondary";
  }
}

function getHealthStatusIcon(status: string) {
  switch (status) {
    case "healthy": return "bi-check-circle-fill";
    case "warning": return "bi-exclamation-triangle-fill";
    case "error": return "bi-x-circle-fill";
    default: return "bi-question-circle-fill";
  }
}

function getStatusBadge(status: string) {
  switch (status) {
    case "active": return "bg-success";
    case "pending": return "bg-warning";
    case "inactive": return "bg-secondary";
    default: return "bg-secondary";
  }
}

function formatDate(dateString: string) {
  return new Date(dateString).toLocaleDateString();
}

function formatTimeAgo(timestamp: string) {
  const now = new Date();
  const activityTime = new Date(timestamp);
  const diffMs = now.getTime() - activityTime.getTime();
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays === 1) return "1 day ago";
  return `${diffDays} days ago`;
}

const rateClass = (rate: number) =>
  rate >= 90 ? "text-success" : rate >= 80 ? "text-warning" : "text-danger";

function handleRowClick(client: PartnerClient) {
  router.push(`/partners/clients/${client.id}`);
}

const tableOptions = {
  columns: [
    { data: "id", title: "ID", visible: false },
    {
      data: "organizationName",
      title: "Organization",
      render: (data: string, _type: string, row: PartnerClient) => {
        const issueHtml =
          row.issues.length > 0
            ? `<div class="small text-danger mt-1"><i class="bi bi-exclamation-triangle me-1"></i>${row.issues.length} issue${row.issues.length !== 1 ? "s" : ""}</div>`
            : "";
        return `<div><strong>${data}</strong>${issueHtml}</div>`;
      },
    },
    {
      data: "contactName",
      title: "Contact",
      render: (_data: string, _type: string, row: PartnerClient) =>
        `<div><div>${row.contactName}</div><small class="text-muted">${row.contactEmail}</small></div>`,
    },
    {
      data: "status",
      title: "Status",
      render: (data: string) =>
        `<span class="badge text-uppercase ${getStatusBadge(data)}">${data}</span>`,
    },
    {
      data: "healthStatus",
      title: "Health",
      render: (data: string) =>
        `<i class="bi ${getHealthStatusIcon(data)} ${getHealthStatusColor(data)}" title="${data}"></i>`,
    },
    {
      data: "projectCount",
      title: "Projects",
      render: (data: number) => `<span class="fw-bold">${data}</span>`,
    },
    {
      data: "successRate",
      title: "Success Rate",
      render: (data: number) => {
        const color = data >= 90 ? "text-success" : data >= 80 ? "text-warning" : "text-danger";
        const barColor = data >= 90 ? "bg-success" : data >= 80 ? "bg-warning" : "bg-danger";
        return `<div class="d-flex align-items-center">
          <span class="fw-bold me-2 ${color}">${data.toFixed(1)}%</span>
          <div class="progress" style="width: 50px; height: 4px;">
            <div class="progress-bar ${barColor}" style="width: ${data}%"></div>
          </div>
        </div>`;
      },
    },
    {
      data: "totalRuns",
      title: "Total Runs",
      render: (data: number) => `<span class="fw-bold">${data.toLocaleString()}</span>`,
    },
    {
      data: "joinedDate",
      title: "Joined",
      render: (data: string) => `<small class="text-muted">${formatDate(data)}</small>`,
    },
    {
      data: "lastActivity",
      title: "Last Activity",
      render: (data: string) => `<small class="text-muted">${formatTimeAgo(data)}</small>`,
    },
    {
      data: null,
      title: "Actions",
      orderable: false,
      render: () =>
        `<div class="d-flex gap-2">
          <button class="bb-btn-secondary-cta btn-sm text-nowrap"><i class="bi bi-eye me-1"></i>View</button>
          <button class="bb-btn-secondary-cta btn-sm text-nowrap"><i class="bi bi-gear me-1"></i>Manage</button>
        </div>`,
    },
  ],
  pageLength: 25,
  layout: {
    topEnd: "",
    bottomEnd: "",
    bottomStart: "info",
  },
} as Config;
</script>

<template>
  <PanelGrid :columns="1">
    <PanelCard>
      <PageHeader
        title="Partner Clients"
        :breadcrumbs="[
          { label: 'Dashboard', href: '/' },
          { label: 'Partners', href: '/partners' },
          { label: 'Clients' },
        ]"
      >
        <template #actions>
          <router-link
            to="/partners/settings"
            class="bb-btn-secondary-cta d-none d-md-inline"
          >
            <i class="bi bi-gear me-2"></i>Partner Settings
          </router-link>
        </template>
      </PageHeader>

      <div class="card mb-4 mt-4">
        <div class="card-body">
          <h2 class="card-title">Search & Filters</h2>
          <div class="row g-3">
            <div class="col-12 col-md-6 col-lg-4">
              <label class="form-label">Search</label>
              <input
                v-model="searchQuery"
                type="text"
                class="form-control"
                placeholder="Search by name, organisation, or email..."
              />
            </div>
            <div class="col-12 col-md-6 col-lg-4">
              <label class="form-label">Status</label>
              <select v-model="selectedStatus" class="form-select">
                <option
                  v-for="option in statusOptions"
                  :key="option.value"
                  :value="option.value"
                >
                  {{ option.label }}
                </option>
              </select>
            </div>
            <div class="col-12 col-md-6 col-lg-4">
              <label class="form-label">Health</label>
              <select v-model="selectedHealth" class="form-select">
                <option
                  v-for="option in healthOptions"
                  :key="option.value"
                  :value="option.value"
                >
                  {{ option.label }}
                </option>
              </select>
            </div>
          </div>
        </div>
      </div>

      <div v-if="isLoading" class="text-center py-5">
        <div class="spinner-border text-primary" role="status">
          <span class="visually-hidden">Loading...</span>
        </div>
      </div>

      <ErrorState
        v-else-if="hasError"
        type="general"
        message="Unable to load your clients. Please try again."
        @retry="refetch"
      />

      <DataView
        v-else
        :data="filteredClients"
        :view-mode="'table'"
        :options="tableOptions"
        @row-click="(client: PartnerClient) => handleRowClick(client)"
      >
        <!-- Mobile card — kept visually identical to the dashboard overview widget
             (PartnerClientsList.vue). If you change one, change both. -->
        <template #grid-card="{ row: client }">
          <div class="card h-100 card-hover">
            <div class="card-body">
              <div class="d-flex justify-content-between align-items-start gap-2 mb-2">
                <div class="min-w-0">
                  <h4 class="mb-0 fw-medium text-truncate">{{ client.organizationName }}</h4>
                  <small class="text-muted">{{ client.contactName }}</small>
                </div>
                <div class="d-flex align-items-center gap-2 flex-shrink-0">
                  <span class="badge rounded-pill text-uppercase" :class="getStatusBadge(client.status)">
                    {{ client.status }}
                  </span>
                  <i
                    :class="[getHealthStatusIcon(client.healthStatus), getHealthStatusColor(client.healthStatus)]"
                    :title="client.healthStatus"
                  ></i>
                </div>
              </div>

              <div class="d-flex flex-wrap column-gap-4 row-gap-1 small">
                <div>
                  <span class="fw-bold">{{ client.projectCount }}</span>
                  <span class="text-muted ms-1">Projects</span>
                </div>
                <div>
                  <span class="fw-bold" :class="rateClass(client.successRate)">
                    {{ client.successRate.toFixed(1) }}%
                  </span>
                  <span class="text-muted ms-1">Success</span>
                </div>
                <div>
                  <span class="fw-bold">{{ client.totalRuns.toLocaleString() }}</span>
                  <span class="text-muted ms-1">Runs</span>
                </div>
              </div>
            </div>

            <div class="card-footer bg-transparent border-top-0 d-flex justify-content-between align-items-center">
              <small class="text-muted"><i class="bi bi-clock me-1"></i>{{ formatTimeAgo(client.lastActivity) }}</small>
              <button class="bb-btn-secondary-cta btn-sm text-nowrap" @click="handleRowClick(client)">
                <i class="bi bi-eye me-1"></i>View
              </button>
            </div>
          </div>
        </template>

        <template #empty-state>
          <div class="text-center py-5">
            <i class="bi bi-people display-1 text-muted"></i>
            <h3 class="mt-3 text-muted">No clients found</h3>
            <p class="text-muted">Try adjusting your filters to see results.</p>
          </div>
        </template>
      </DataView>
    </PanelCard>
  </PanelGrid>
</template>
